import { useState } from 'react'
import { CalendarClock } from 'lucide-react'

import { IrrigationRunsList } from './irrigation-runs-list'
import { IrrigationScheduleForm } from './irrigation-schedule-form'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import { Button } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import { useIrrigationSchedule } from '@/hooks/use-irrigation-schedule-query'
import { m } from '@/lib/paraglide/messages'

/** Nút "Lịch tưới" và panel cấu hình lịch + danh sách phiên của một bộ tưới. */
export function IrrigationScheduleSheet({
    device,
    canEdit,
    buttonClassName,
}: {
    device: IrrigationDevice
    canEdit: boolean
    /** Lớp thêm cho nút mở lịch (vd nút cao hơn trên điện thoại ở thẻ trạm). */
    buttonClassName?: string
}) {
    const [open, setOpen] = useState(false)
    const {
        data: saved,
        isLoading,
        isError,
        refetch,
    } = useIrrigationSchedule(device.id, open)

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                className={buttonClassName}
                onClick={() => setOpen(true)}
            >
                <CalendarClock className="size-4" />
                {m.irrigation_schedule_button()}
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="w-full overflow-y-auto sm:max-w-xl"
                >
                    <SheetHeader>
                        {/* Chừa chỗ cho nút đóng ở góc: tên trạm dài không đè lên. */}
                        <SheetTitle className="pr-8">
                            {m.irrigation_schedule_title({ name: device.name })}
                        </SheetTitle>
                        <SheetDescription>
                            {m.irrigation_schedule_desc()}
                        </SheetDescription>
                    </SheetHeader>
                    <div className="space-y-6 pb-6">
                        {isLoading ? (
                            <p className="text-sm text-muted-foreground">
                                {m.common_loading()}
                            </p>
                        ) : isError ? (
                            // Không hiện form khi chưa đọc được lịch thật: form
                            // mặc định "Không lịch", lưu lúc đó sẽ xoá lịch đang có.
                            <div className="space-y-2">
                                <p className="text-sm text-destructive">
                                    {m.irrigation_schedule_load_error()}
                                </p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void refetch()}
                                >
                                    {m.irrigation_schedule_retry()}
                                </Button>
                            </div>
                        ) : (
                            <IrrigationScheduleForm
                                device={device}
                                saved={saved}
                                canEdit={canEdit}
                            />
                        )}
                        <IrrigationRunsList
                            device={device}
                            canEdit={canEdit}
                            enabled={open}
                        />
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}
